import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import HTLDSlide4Mobile from "./HTLDSlide4Mobile";
import { useVisibleAnimation } from "./useVisibleAnimation";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { slide4Content, useSlide4, Seg } from "./slide4.data";

const accent = "#d4537e";
const font   = FONT_BODY;

const S: Record<string, CSSProperties> = {
root:        { background: "#040f18", fontFamily: font, color: "#d0e8f8", width: "100%", height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box", overflow: "hidden" },
// Shared header row: breadcrumb (left) + slide title (right), above the divider line
subtitleBar: {
background: "#050f1a", borderBottom: "1px solid rgba(212,83,126,0.1)",
padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
display: "flex", alignItems: "center", justifyContent: "space-between",
gap: "clamp(12px,2vw,28px)", flexShrink: 0,
},
body:        { flex: 1, minHeight: 0, display: "flex", gap: "clamp(16px,2vw,28px)", padding: "clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)", alignItems: "stretch", overflow: "hidden" },
content:     { flex: 1, minWidth: "min(100%, 400px)", display: "flex", flexDirection: "column", justifyContent: "space-between" },
panel:       { width: "clamp(220px,28vw,390px)", flexShrink: 0, display: "flex", flexDirection: "column" },
};

// ─── Dream Canvas ─────────────────────────────────────────────────────────────
const DreamCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawFnRef = useRef<null | (() => void)>(null);
  const dtRef     = useRef<number>(0);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
    const W = 420, H = 560; canvas.width = W; canvas.height = H;
    const cx = 210, cy = 185, sphereR = 108;
    const c = pick(slide4Content).canvas; // slides remount on language change
    let frame = 0;
  
    // ── Static data ──────────────────────────────────────────────────────────
  
    // Twinkling starfield (was 12 static dots)
    const bgStars = Array.from({ length: 110 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: 0.3 + Math.random() * 1.4,
      tw: Math.random() * Math.PI * 2, spd: 0.010 + Math.random() * 0.024,
    }));
  
    const nodes = [
      {x:cx-52,y:cy-28},{x:cx+42,y:cy-52},{x:cx-18,y:cy+32},{x:cx+62,y:cy+12},
      {x:cx-62,y:cy+22},{x:cx+10,y:cy-72},{x:cx+36,y:cy+58},{x:cx-36,y:cy-62},
      {x:cx+78,y:cy-22},{x:cx-78,y:cy-8},
    ];
    const conns = [
      [0,1],[1,5],[5,3],[3,6],[6,2],[2,4],[4,7],[7,0],
      [1,3],[0,5],[2,6],[4,9],[8,3],[8,1],[9,7],[0,2],
    ];
    const pulse = conns.map((_, i) => ({ b: 0, off: i * 12 }));
  
    const orbs = [
      {r:138,spd: 0.008,ph:0,sz:8},
      {r:150,spd:-0.006,ph:Math.PI/3,sz:5},
      {r:156,spd: 0.010,ph:Math.PI*2/3,sz:6},
      {r:142,spd:-0.007,ph:Math.PI,sz:4},
      {r:162,spd: 0.005,ph:Math.PI*4/3,sz:7},
      {r:133,spd:-0.009,ph:Math.PI*5/3,sz:5},
    ];
  
    // Orbital trails for memory orbs
    const TRAIL = 22;
    const trails = orbs.map(() => [] as {x:number,y:number}[]);
  
    // Dream bubbles float from face toward sphere
    const bubbles = Array.from({ length: 14 }, () => ({
      x: cx + (Math.random() - 0.5) * 60,
      y: 370 + Math.random() * 35,
      r: 1.5 + Math.random() * 4,
      speed: 0.30 + Math.random() * 0.55,
      life: Math.random(),
      wobble: Math.random() * Math.PI * 2,
    }));
  
    // Ambient dream particles throughout canvas
    const particles = Array.from({ length: 35 }, () => ({
      x: 30 + Math.random() * (W - 60),
      y: Math.random() * H,
      r: 0.5 + Math.random() * 1.4,
      vx: (Math.random() - 0.5) * 0.25,
      vy: -(0.08 + Math.random() * 0.25),
      life: Math.random(),
      lspd: 0.002 + Math.random() * 0.005,
      col: Math.random() < 0.65 ? "212,83,126" : "140,80,220",
    }));
  
    // ── Face (unchanged from original) ──────────────────────────────────────
    function drawCSSFace(hx: number, hy: number, fr: number) {
      const floatOff = Math.sin(fr * 0.018) * 4; // gentle float
      const hy2 = hy + floatOff;
      const SVG_SCALE = 1.35, SVG_CX = 28.3, SVG_HEAD_CY = 34;
      const tx = hx - SVG_CX * SVG_SCALE;
      const ty = hy2 - SVG_HEAD_CY * SVG_SCALE;
  
      const aGlow = ctx.createRadialGradient(hx, hy2, 35, hx, hy2, 95);
      aGlow.addColorStop(0, "rgba(212,83,126,0.32)"); aGlow.addColorStop(1, "rgba(212,83,126,0)");
      ctx.beginPath(); ctx.ellipse(hx, hy2, 72, 78, 0, 0, Math.PI*2); ctx.fillStyle = aGlow; ctx.fill();
  
      ctx.save();
      ctx.translate(tx, ty); ctx.scale(SVG_SCALE, SVG_SCALE);
      ctx.beginPath(); ctx.rect(0, 0, 56.594, 68); ctx.clip();
  
      const headPath = new Path2D(
        "M15.92 68.5l8.8 12.546 3.97 13.984-9.254-7.38-4.622-15.848zm27.1 0l-8.8 12.546-3.976 13.988 9.254-7.38 4.622-15.848zm6.11-27.775l.108-11.775-21.16-14.742L8.123 26.133 8.09 40.19l-3.24.215 1.462 9.732 5.208 1.81 2.36 11.63 9.72 11.018 10.856-.324 9.56-10.37 1.918-11.952 5.207-1.81 1.342-9.517zm-43.085-1.84l-.257-13.82L28.226 11.9l23.618 15.755-.216 10.37 4.976-17.085L42.556 2.376 25.49 0 10.803 3.673.002 24.415z"
      );
      const fGrd = ctx.createRadialGradient(SVG_CX-10, SVG_HEAD_CY-12, 3, SVG_CX+3, SVG_HEAD_CY+3, 31);
      fGrd.addColorStop(0,"#f0a8c0"); fGrd.addColorStop(0.45,"#d4537e"); fGrd.addColorStop(0.82,"#a03060"); fGrd.addColorStop(1,"#701838");
      ctx.fillStyle = fGrd; ctx.fill(headPath);
      const edge = ctx.createRadialGradient(SVG_CX, SVG_HEAD_CY, 22, SVG_CX, SVG_HEAD_CY, 33);
      edge.addColorStop(0,"rgba(0,0,0,0)"); edge.addColorStop(1,"rgba(0,0,0,0.22)");
      ctx.fillStyle = edge; ctx.fill(headPath);
      const gloss = ctx.createRadialGradient(SVG_CX-9, SVG_HEAD_CY-14, 1, SVG_CX-5, SVG_HEAD_CY-6, 16);
      gloss.addColorStop(0,"rgba(255,220,235,0.50)"); gloss.addColorStop(1,"rgba(255,220,235,0)");
      ctx.fillStyle = gloss; ctx.fill(headPath);
      ctx.strokeStyle = "rgba(212,83,126,0.55)"; ctx.lineWidth = 1/SVG_SCALE; ctx.stroke(headPath);
      ctx.restore();
  
      ctx.strokeStyle = "#3a0818"; ctx.lineWidth = 3; ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(hx-24, hy2+5); ctx.lineTo(hx-8,  hy2+5); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(hx+8,  hy2+5); ctx.lineTo(hx+24, hy2+5); ctx.stroke();
  
      const floatY = Math.sin(fr * 0.04) * 8;
      const floatA = (0.5 + 0.5 * Math.sin(fr * 0.04)).toFixed(2);
      ctx.font = "bold 26px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = `rgba(255,220,235,${floatA})`;
      ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
      ctx.fillText("Zzz", hx + 34, hy2 - 48 + floatY);
    }
  
    function draw() {
      ctx.clearRect(0, 0, W, H);
  
      // ── 1. Background ──────────────────────────────────────────────────────
      const bgGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy * 1.5, W);
      bgGrd.addColorStop(0, "#0a0418"); bgGrd.addColorStop(0.5, "#050212"); bgGrd.addColorStop(1, "#010108");
      ctx.fillStyle = bgGrd; ctx.fillRect(0, 0, W, H);
  
      // Nebula clouds — rose + purple variety
      ([
        [75,  55,  145, "rgba(110,25,70,0.25)"],
        [355, 85,  120, "rgba(55,18,140,0.20)"],
        [45,  445, 165, "rgba(90,12,100,0.18)"],
        [385, 460, 130, "rgba(35,25,160,0.16)"],
        [210, 520, 100, "rgba(90,15,60,0.14)"],
      ] as [number,number,number,string][]).forEach(([nx,ny,nr,nc]) => {
        const g = ctx.createRadialGradient(nx,ny,0,nx,ny,nr);
        g.addColorStop(0, nc); g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.beginPath(); ctx.arc(nx,ny,nr,0,Math.PI*2); ctx.fillStyle=g; ctx.fill();
      });
  
      // Twinkling stars
      bgStars.forEach(s => {
        const tw = 0.28 + 0.72 * Math.abs(Math.sin(frame * s.spd + s.tw));
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(255,255,255,${(tw*0.72).toFixed(2)})`; ctx.fill();
      });
  
      // Moon
      [[22,0.07],[15,0.13],[10,1]].forEach(([r,a],i) => {
        ctx.beginPath(); ctx.arc(385,44,r,0,Math.PI*2);
        ctx.fillStyle = i < 2 ? `rgba(255,252,180,${a})` : "#fffde7"; ctx.fill();
      });
  
      const gPulse = 0.5 + 0.5 * Math.sin(frame * 0.022);
  
      // ── 2. Ambient dream particles ────────────────────────────────────────
      particles.forEach(dp => {
        dp.x += dp.vx; dp.y += dp.vy; dp.life += dp.lspd;
        if (dp.life > 1 || dp.y < -10) {
          dp.y = H * 0.7 + Math.random() * H * 0.3; dp.x = 30 + Math.random() * (W-60); dp.life = 0;
        }
        const a = Math.sin(dp.life * Math.PI) * 0.42;
        if (a > 0.01) {
          ctx.beginPath(); ctx.arc(dp.x, dp.y, dp.r, 0, Math.PI*2);
          ctx.fillStyle = `rgba(${dp.col},${a.toFixed(2)})`; ctx.fill();
        }
      });
  
      // ── 3. Brain wave rings ───────────────────────────────────────────────
      for (let w = 0; w < 4; w++) {
        const wp = ((frame * 0.45 + w * 45) % 135) / 135;
        ctx.beginPath(); ctx.arc(cx, cy, sphereR + 20 + wp * 110, 0, Math.PI*2);
        ctx.strokeStyle = `rgba(212,83,126,${((1-wp) * 0.16).toFixed(3)})`; ctx.lineWidth = 1.2; ctx.stroke();
      }
  
      // ── 4. Sphere glow (layered) ──────────────────────────────────────────
      const oGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, sphereR + 72);
      oGrd.addColorStop(0,    `rgba(212,83,126,${0.32+0.14*gPulse})`);
      oGrd.addColorStop(0.40, `rgba(180,55,115,${0.14+0.05*gPulse})`);
      oGrd.addColorStop(0.72, "rgba(212,83,126,0.04)");
      oGrd.addColorStop(1,    "rgba(212,83,126,0)");
      ctx.beginPath(); ctx.arc(cx, cy, sphereR+72, 0, Math.PI*2); ctx.fillStyle=oGrd; ctx.fill();
  
      // ── 5. Sphere body ────────────────────────────────────────────────────
      const sGrd = ctx.createRadialGradient(cx-28, cy-28, 5, cx, cy, sphereR);
      sGrd.addColorStop(0,    `rgba(255,155,200,${0.32+0.10*gPulse})`);
      sGrd.addColorStop(0.38, `rgba(212,83,126,${0.20+0.06*gPulse})`);
      sGrd.addColorStop(0.72, "rgba(90,20,55,0.12)");
      sGrd.addColorStop(1,    "rgba(212,83,126,0.04)");
      ctx.beginPath(); ctx.arc(cx, cy, sphereR, 0, Math.PI*2); ctx.fillStyle=sGrd; ctx.fill();
  
      // ── 6. Rotating sphere grid (gives 3-D brain orb feel) ───────────────
      const rot = frame * 0.006;
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, sphereR-1, 0, Math.PI*2); ctx.clip();
      for (let i = 0; i < 6; i++) {
        const a = (i/6)*Math.PI + rot;
        ctx.beginPath();
        ctx.ellipse(cx, cy, sphereR * Math.abs(Math.cos(a)), sphereR, 0, 0, Math.PI*2);
        ctx.strokeStyle = `rgba(212,83,126,${(0.07+0.04*Math.abs(Math.cos(a*2))).toFixed(3)})`; ctx.lineWidth = 0.8; ctx.stroke();
      }
      for (let j = 1; j < 4; j++) {
        const lat = (j/4) * sphereR;
        const rLat = Math.sqrt(Math.max(0, sphereR*sphereR - lat*lat));
        [-1,1].forEach(sign => {
          ctx.beginPath(); ctx.ellipse(cx, cy + sign*lat, rLat, rLat*0.28, rot, 0, Math.PI*2);
          ctx.strokeStyle = "rgba(212,83,126,0.065)"; ctx.lineWidth = 0.65; ctx.stroke();
        });
      }
      ctx.restore();
  
      // Sphere border — double ring
      ctx.beginPath(); ctx.arc(cx, cy, sphereR, 0, Math.PI*2);
      ctx.strokeStyle = `rgba(212,83,126,${(0.12+0.06*gPulse).toFixed(3)})`; ctx.lineWidth = 8; ctx.stroke();
      ctx.strokeStyle = `rgba(212,83,126,${(0.50+0.18*gPulse).toFixed(3)})`; ctx.lineWidth = 1.5; ctx.stroke();
  
      // ── 7. Neural connections — double-pass for glow ──────────────────────
      conns.forEach((conn, i) => {
        const p = pulse[i]; const t = (frame + p.off) % 200;
        p.b = t < 100 ? Math.sin((t/100)*Math.PI) : 0;
        const n1 = nodes[conn[0]], n2 = nodes[conn[1]];
        // Wide glow
        ctx.beginPath(); ctx.moveTo(n1.x,n1.y); ctx.lineTo(n2.x,n2.y);
        ctx.strokeStyle = `rgba(212,83,126,${(0.06+p.b*0.18).toFixed(3)})`; ctx.lineWidth = 4+p.b*4; ctx.stroke();
        // Core
        ctx.beginPath(); ctx.moveTo(n1.x,n1.y); ctx.lineTo(n2.x,n2.y);
        ctx.strokeStyle = `rgba(212,83,126,${(0.14+p.b*0.58).toFixed(3)})`; ctx.lineWidth = 0.8+p.b*1.8; ctx.stroke();
        // Traveling spark
        if (p.b > 0.08) {
          const frac = ((frame+p.off)%100)/100;
          ctx.beginPath(); ctx.arc(n1.x+(n2.x-n1.x)*frac, n1.y+(n2.y-n1.y)*frac, 3, 0, Math.PI*2);
          ctx.fillStyle = `rgba(255,185,215,${(p.b*0.90).toFixed(3)})`; ctx.fill();
        }
      });
  
      // ── 8. Nodes ──────────────────────────────────────────────────────────
      nodes.forEach((n, i) => {
        const np = 0.5 + 0.5 * Math.sin(frame*0.032 + i*0.75);
        const ng = ctx.createRadialGradient(n.x,n.y,0,n.x,n.y,14);
        ng.addColorStop(0, `rgba(212,83,126,${(0.28+np*0.22).toFixed(3)})`); ng.addColorStop(1,"rgba(212,83,126,0)");
        ctx.beginPath(); ctx.arc(n.x,n.y,14,0,Math.PI*2); ctx.fillStyle=ng; ctx.fill();
        ctx.beginPath(); ctx.arc(n.x,n.y,3.5+np*1.5,0,Math.PI*2);
        ctx.fillStyle = `rgba(255,150,190,${(0.78+np*0.20).toFixed(3)})`; ctx.fill();
      });
  
      // REM label with glow
      ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
      ctx.font = "bold 13px 'JetBrains Mono','Courier New',monospace";
      ctx.shadowColor = "rgba(212,83,126,0.65)"; ctx.shadowBlur = 10;
      ctx.fillStyle = `rgba(255,185,215,${(0.65+0.25*gPulse).toFixed(3)})`;
      ctx.fillText(c.rem, cx, cy - sphereR + 18);
      ctx.shadowBlur = 0;
  
      // ── 9. Orbit path ─────────────────────────────────────────────────────
      ctx.beginPath(); ctx.arc(cx, cy, 148, 0, Math.PI*2);
      ctx.strokeStyle = "rgba(212,83,126,0.11)"; ctx.lineWidth = 1;
      ctx.setLineDash([3,8]); ctx.stroke(); ctx.setLineDash([]);
  
      // ── 10. Memory orbs with comet trails ────────────────────────────────
      orbs.forEach((orb, i) => {
        const ang = frame * orb.spd + orb.ph;
        const ox = cx + orb.r * Math.cos(ang), oy = cy + orb.r * Math.sin(ang);
  
        // Update trail
        trails[i].unshift({x:ox, y:oy});
        if (trails[i].length > TRAIL) trails[i].pop();
  
        // Draw trail
        trails[i].forEach((tp, ti) => {
          const ta = (1 - ti/TRAIL) * 0.38;
          const tr = orb.sz * (1 - ti/TRAIL) * 0.55;
          if (tr > 0.3) {
            ctx.beginPath(); ctx.arc(tp.x, tp.y, tr, 0, Math.PI*2);
            ctx.fillStyle = `rgba(212,83,126,${ta.toFixed(3)})`; ctx.fill();
          }
        });
  
        const op = 0.5 + 0.5 * Math.sin(frame*0.04 + i*1.3);
        const og = ctx.createRadialGradient(ox,oy,0,ox,oy,orb.sz+12);
        og.addColorStop(0, `rgba(255,145,185,${(0.58+0.25*op).toFixed(3)})`); og.addColorStop(1,"rgba(212,83,126,0)");
        ctx.beginPath(); ctx.arc(ox,oy,orb.sz+12,0,Math.PI*2); ctx.fillStyle=og; ctx.fill();
        ctx.beginPath(); ctx.arc(ox,oy,orb.sz*(0.85+0.15*op),0,Math.PI*2);
        ctx.fillStyle = `rgba(255,168,205,${(0.80+0.18*op).toFixed(3)})`; ctx.fill();
        ctx.strokeStyle = "rgba(255,210,230,0.85)"; ctx.lineWidth = 0.9; ctx.stroke();
      });
  
      // ── 11. Neural pathways (face → sphere) — much brighter ──────────────
      const headY = 420;
      for (let i = 0; i < 3; i++) {
        const off = i*28, pp = ((frame+off)%88)/88;
        const bx = cx+(i-1)*20, ex = cx+(i-1)*7, ey = cy+sphereR+2;
        const pathA = 0.20 + 0.10 * Math.sin(frame*0.02+i);
        // Glow pass
        ctx.beginPath();
        ctx.moveTo(bx, headY-55);
        ctx.bezierCurveTo(bx-(i-1)*10, headY-130, ex+(i-1)*8, ey+60, ex, ey);
        ctx.strokeStyle = `rgba(212,83,126,${pathA.toFixed(3)})`; ctx.lineWidth = 5; ctx.stroke();
        // Core pass
        ctx.beginPath();
        ctx.moveTo(bx, headY-55);
        ctx.bezierCurveTo(bx-(i-1)*10, headY-130, ex+(i-1)*8, ey+60, ex, ey);
        ctx.strokeStyle = `rgba(255,140,180,${(pathA*1.4).toFixed(3)})`; ctx.lineWidth = 1.2; ctx.stroke();
        // Traveling particle
        const fpx = bx+(ex-bx)*pp + Math.sin(pp*Math.PI)*(i-1)*12;
        const fpy = (headY-55)+(ey-(headY-55))*pp;
        ctx.beginPath(); ctx.arc(fpx, fpy, 3.5, 0, Math.PI*2);
        ctx.fillStyle = `rgba(255,190,220,${(0.85*Math.sin(pp*Math.PI)).toFixed(3)})`; ctx.fill();
      }
  
      // ── 12. Dream bubbles (float toward sphere) ───────────────────────────
      bubbles.forEach(b => {
        b.y -= b.speed; b.life += 0.009; b.wobble += 0.04;
        b.x += 0.35 * Math.sin(b.wobble);
        if (b.life > 1 || b.y < cy + sphereR + 15) {
          b.y = 368 + Math.random() * 32;
          b.x = cx + (Math.random()-0.5)*58;
          b.life = 0; b.speed = 0.28+Math.random()*0.55;
        }
        const a = Math.sin(b.life * Math.PI) * 0.42;
        const bg = ctx.createRadialGradient(b.x,b.y,0,b.x,b.y,b.r*2.2);
        bg.addColorStop(0, `rgba(255,160,200,${(a*0.45).toFixed(3)})`); bg.addColorStop(1,"rgba(212,83,126,0)");
        ctx.beginPath(); ctx.arc(b.x,b.y,b.r*2.2,0,Math.PI*2); ctx.fillStyle=bg; ctx.fill();
        ctx.beginPath(); ctx.arc(b.x,b.y,b.r,0,Math.PI*2);
        ctx.strokeStyle = `rgba(255,185,215,${a.toFixed(3)})`; ctx.lineWidth = 0.8; ctx.stroke();
        // catch-light
        ctx.beginPath(); ctx.arc(b.x-b.r*0.3, b.y-b.r*0.3, b.r*0.25, 0, Math.PI*2);
        ctx.fillStyle = `rgba(255,235,245,${(a*0.60).toFixed(3)})`; ctx.fill();
      });
  
      // ── 13. Sleeping face ─────────────────────────────────────────────────
      drawCSSFace(cx, headY, frame);
  
      // ── 14. Label ─────────────────────────────────────────────────────────
      ctx.shadowColor = "rgba(212,83,126,0.45)"; ctx.shadowBlur = 7;
      ctx.font = "16px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = "rgba(212,83,126,0.50)";
      ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
      ctx.fillText(c.dreaming, cx, headY + 116);
      ctx.shadowBlur = 0;
  
      frame += dtRef.current > 0 ? dtRef.current * 60 : 1;
    }
  
    drawFnRef.current = draw;
    return () => { drawFnRef.current = null; };
  }, []);
  
  useVisibleAnimation(canvasRef, (dt) => { dtRef.current = dt; drawFnRef.current?.(); }, { fps: 30 });
  
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <canvas
        ref={canvasRef}
        style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }}
      />
    </div>
  );
  };

// ─── Slide content (authored at the ScaleToFit design size) ────────────────────
const HTLDSlide4Inner: React.FC = () => {
const { meta, facts, perspective } = useSlide4();
const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((sg, i) =>
    sg.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{sg.t}</span>
    : sg.em ? <em key={i}>{sg.t}</em>
    : <React.Fragment key={i}>{sg.t}</React.Fragment>
  );
const fontLinkRef = useRef<HTMLLinkElement | null>(null);

useEffect(() => {
const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
if (!existing) {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
  document.head.appendChild(link);
  fontLinkRef.current = link;
}
return () => {
  if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
    document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
  }
};
}, []);

const lbl: CSSProperties = { fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO, color: accent, letterSpacing: 3, textTransform: "uppercase", marginBottom: "clamp(4px,0.55vw,8px)", fontStyle: "normal" };
const txt: CSSProperties = { fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontFamily: FONT_BODY, lineHeight: 1.7, color: "#cde0f2", margin: 0 };

const rootRef = useRef<HTMLDivElement>(null);

return (
<div style={S.root} ref={rootRef}>      {/* add ref={rootRef} */}
<SideStarField rootRef={rootRef} />   {/* add this line */}

  {/* Header row: breadcrumb (left) + slide title (right), above the horizontal line */}
  <div style={S.subtitleBar}>
    <div style={{
      fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc",
      fontFamily: FONT_BODY,
    }}>
      {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
      <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
    </div>
    <div style={{
      fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600,
      color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1,
      whiteSpace: "nowrap",
    }}>
      {meta.headerPlain} <span style={{ color: accent }}>{meta.headerAccent}</span>
    </div>
  </div>

  <div style={S.body}>
    <div style={S.content}>
      <div>
        <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(20px,2.4vw,32px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(2px,0.35vw,6px)", lineHeight: 1.2 }}>
          {meta.titlePlain} <span style={{ color: accent }}>{meta.titleAccent}</span>
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.1vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#7aa8cc", margin: 0, letterSpacing: 0.3 }}>
          {meta.subtitle}
        </p>
      </div>
      <div style={{ borderBottom: "1px solid rgba(212,83,126,0.08)", paddingBottom: "clamp(8px,1vw,14px)" }}>
        <div style={lbl}>{meta.whatLabel}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "clamp(6px,0.8vw,10px)" }}>
          {facts.map((f, i) => (
            <div key={i} style={{ display: "flex", gap: "clamp(10px,1.2vw,16px)", alignItems: "flex-start", background: "rgba(212,83,126,0.04)", border: "1px solid rgba(212,83,126,0.10)", borderRadius: 8, padding: "clamp(8px,1vw,13px)" }}>
              <div style={{ fontSize: "clamp(13px,1.3vw,18px)", fontWeight: 700, color: "rgba(212,83,126,0.30)", fontFamily: FONT_MONO, flexShrink: 0, lineHeight: 1 }}>{f.num}</div>
              <div>
                <div style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontWeight: 700, color: "#f0d8e0", marginBottom: "clamp(2px,0.3vw,4px)" }}>{f.title}</div>
                <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.95vw,14px)", fontStyle: "normal", fontWeight: 300, lineHeight: 1.6, color: "#9bbdd6", margin: 0 }}>{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: "rgba(212,83,126,0.06)", border: "1px solid rgba(212,83,126,0.20)", borderRadius: 8, padding: "clamp(11px,1.3vw,18px) clamp(13px,1.5vw,20px)" }}>
        <div style={lbl}>{meta.perspectiveLabel}</div>
        <p style={{ ...txt, color: "#f0c8d8" }}>{renderSegs(perspective, "#ffffff")}</p>
      </div>
    </div>

    {/* Panel — dream canvas fills the full panel height */}
    <div style={S.panel}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <DreamCanvas />
      </div>
    </div>
  </div>

</div>
);
};

// Scale the whole slide as one unit so it looks identical across screen
// resolutions, window sizes, and browser zoom — no reflow, no clipping.
const HTLDSlide4: React.FC = () => {
  const portrait = useIsPortrait();
  return portrait
    ? <HTLDSlide4Mobile />
    : <ScaleToFit><HTLDSlide4Inner /></ScaleToFit>;
};

export default HTLDSlide4;